import { useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { InformationBox, TextLoadingPlaceholder } from '@app/core';
import { Invoice, useCreateInvoicePaymentIntent } from '@app/api';
import { toEuros } from 'lib/numbers';
import { StripeProvider } from 'components';
import { PaymentMethodContainer } from 'components/BillingInformation/PaymentMethodContainer';
import { PaymentIntentForm } from './PaymentIntentForm';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  box: {
    marginBottom: theme.marginBase * 3,
  },
}));

interface Props {
  invoice: Invoice;
  onSuccess?: (invoice: Invoice) => void;
}

export const InvoicePaymentForm = ({ invoice, onSuccess }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    mutate: createIntent,
    data: intent,
    error,
  } = useCreateInvoicePaymentIntent(invoice.id);

  useEffect(() => {
    createIntent();
  }, [createIntent]);

  const renderContent = () => {
    if (error) {
      return (
        <InformationBox
          text={t('billing.paymentMethod.intentStatus.creationError')}
          variant="danger"
        />
      );
    }
    if (!intent?.client_secret) {
      return (
        <PaymentMethodContainer>
          <TextLoadingPlaceholder sample="Visa 1231 1234 1234 1200" />
        </PaymentMethodContainer>
      );
    }
    return (
      <StripeProvider clientSecret={intent.client_secret}>
        <PaymentIntentForm
          invoice={invoice}
          clientSecret={intent.client_secret}
          onSuccess={onSuccess}
          intent={intent}
        />
      </StripeProvider>
    );
  };

  return (
    <div>
      <h2 className={classes.title}>{t('billing.payInvoice.formTitle')}</h2>

      <InformationBox
        text={t('billing.payInvoice.formInfo', {
          amount: toEuros(invoice.total || 0, true),
        })}
        className={classes.box}
      />

      {renderContent()}
    </div>
  );
};
